import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, of } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const CURRENT_MODE_KEY = 'application_mode';
const THEME_MODE_KEY = 'application_theme_mode';
const THEME_MODE_CSS_CLASS = 'dark-mode';
const THEME_MODE_TOGGLE_SHORTCUT_KEY = 'd';

export enum ApplicationMode {
  Sandbox = 'Sandbox',
  Live = 'Live',
}

export enum ThemeMode {
  Light = 'Light',
  Dark = 'Dark',
}

@Injectable({ providedIn: 'root' })
export class AppService {
  private shortcuts: { key: string; handler: () => void }[] = [];
  private applicationMode = new BehaviorSubject<ApplicationMode>(this.getInitialApplicationMode());
  private theme = new BehaviorSubject<ThemeMode>(this.getInitialTheme());

  public applicationMode$ = this.applicationMode.asObservable();
  public theme$ = this.theme.asObservable();

  constructor(
    private readonly cookieService: CookieService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.theme$.pipe(takeUntilDestroyed()).subscribe(theme => this.updateThemeMode(theme));

    this.shortcuts = [{ key: THEME_MODE_TOGGLE_SHORTCUT_KEY, handler: this.toggleThemeMode }];
  }

  getBaseUrl() {
    const mode = this.applicationMode.getValue();
    return mode == ApplicationMode.Live ? environment.productionApiUrl : environment.stagingApiUrl;
  }

  getTenantBaseUrl() {
    return environment.tenantManagementApiURL;
  }

  getApplicationMode() {
    return this.applicationMode.getValue();
  }

  getApplicationModePrefix() {
    const mode = this.applicationMode.getValue();
    return mode === ApplicationMode.Live ? 'p' : 's';
  }

  setApplicationMode(mode: ApplicationMode) {
    this.applicationMode.next(mode);
    this.cookieService.set(CURRENT_MODE_KEY, mode, { path: '/' });
    return of(mode);
  }

  toggleThemeMode() {
    const currentTheme = this.theme.getValue() || ThemeMode.Light;
    const newTheme = currentTheme === ThemeMode.Dark ? ThemeMode.Light : ThemeMode.Dark;
    this.setThemeMode(newTheme);
  }

  setThemeMode(theme: ThemeMode | string) {
    // For some reason theme comes with lowercase
    if (theme === 'dark') theme = ThemeMode.Dark;
    if (theme === 'light') theme = ThemeMode.Light;
    this.theme.next(theme as ThemeMode);
  }

  handleKeyShortcuts(event: KeyboardEvent) {
    const shortcut = this.shortcuts.find(s => s.key === event.key);
    if (this.isMetaOrCtrl(event) && shortcut) {
      event.preventDefault();
      shortcut?.handler.call(this);
    }
  }

  private getInitialApplicationMode(): ApplicationMode {
    return (this.cookieService.get(CURRENT_MODE_KEY) as ApplicationMode) || ApplicationMode.Sandbox;
  }

  private getInitialTheme(): ThemeMode {
    return (this.cookieService.get(THEME_MODE_KEY) as ThemeMode) || ThemeMode.Light;
  }

  private updateThemeMode(theme: ThemeMode) {
    this.cookieService.set(THEME_MODE_KEY, theme, { path: '/' });
    const cls = this.document.body.classList;
    theme === ThemeMode.Dark ? cls.add(THEME_MODE_CSS_CLASS) : cls.remove(THEME_MODE_CSS_CLASS);
  }

  private isMetaOrCtrl(event: KeyboardEvent) {
    return event.metaKey || event.ctrlKey;
  }
}
